import { useHistory } from 'react-router-dom';
import LeftRightCard, { Copy, ImagePart, Prompt, TextPart, Title } from '../components/LeftRightCard/LeftRightCard';
import SectionHeader from '../components/SectionHeader';
import AppleAppStoreBadge from '../components/AppleAppStoreBadge';
import GooglePlayStoreBadge from '../components/PlayStoreBadge/PlayStoreBadge';
import cx from 'classnames';
import checkmarkCircleGrey from '../images/pkp-checkmark-in-circle-grey.svg';
import elaAndSelImage from '../images/mockup-k-8.png';
import elementarySolutionsImage from '../images/new-mockup-k-5.png';
import secondarySolutionsImage from '../images/home-left-right-2.png';
import dataUsageImage from '../images/mockup-data.png';
import styles from '../SCSS/HighlightsSegment.module.scss';

const defaultProps = {
  elaAndSel: true,
  elementarySolutions: true,
  middleSchoolSolutions: true,
  systemWideData: true,
};

interface Props {
  elaAndSel: boolean;
  elementarySolutions: boolean;
  middleSchoolSolutions: boolean;
  systemWideData: boolean;
}

const HighlightsSegment = ({
  elaAndSel,
  elementarySolutions,
  middleSchoolSolutions,
  systemWideData,
}: Props & typeof defaultProps): JSX.Element => {
  const history = useHistory();
  return (
    <section className={styles.content}>
      <div className={styles.verticalWhiteSpace} />
      {elaAndSel && (
        <LeftRightCard textLeadEven>
          <ImagePart>
            <img className={styles.imageMaxWidth} src={elaAndSelImage} alt='mockup pk-12' />
          </ImagePart>
          <div className={cx(styles.leftMarginHack, styles.elaAndSelCard)}>
            <TextPart>
              <SectionHeader
                alignLeft
                preTitle='BUILDING WELL-BEING SKILLS'
                title='Resilient Students Lead to Healthier Schools'
              />
              <Copy>
                <p>With Peekapak, you will:</p>
                <ul className={styles.lists}>
                  <li>
                    <img src={checkmarkCircleGrey} alt='checkmark icon' />
                    Support Educators with Easy-to-Use Wellness Resources
                  </li>
                  <li>
                    <img src={checkmarkCircleGrey} alt='checkmark icon' />
                    Build Emotional Resilience & Reduce Student Stress
                  </li>
                  <li>
                    <img src={checkmarkCircleGrey} alt='checkmark icon' />
                    Improve Classroom Climate & Reduces Behavioral Challenges
                  </li>
                  <li>
                    <img src={checkmarkCircleGrey} alt='checkmark icon' />
                    Boost Confidence, Self-Awareness & Positive Relationships
                  </li>
                </ul>
              </Copy>
              <Prompt clickHandler={() => history.push('/requestInfo')}>Request a Quote</Prompt>
            </TextPart>
          </div>
        </LeftRightCard>
      )}
      <div className={styles.verticalWhiteSpace} />
      {elementarySolutions && (
        <LeftRightCard imageLead>
          <ImagePart>
            <img className={styles.imageMaxWidth} src={elementarySolutionsImage} alt='classroom interaction' />
          </ImagePart>
          <TextPart>
            <Copy>
              <h3 className={styles.preTitle}>PRE-K TO 5TH GRADE</h3>
            </Copy>
            <Title>Helping Kids Understand & Manage Emotions</Title>
            <Copy>
              <p>
                Our easy-to-use, educator-led curriculum features levelled readers and fully-planned lessons to help
                students develop empathy, self-regulation and more while also developing core academic skills, including
                reading, writing, listening, speaking, representing and problem-solving.
              </p>
              <p>
                In myPeekaville, Peekapak&apos;s elementary digital learning game, students experience SEL concepts in
                the context of an interactive story-world, filled with quests, games, embedded with extrinsic rewards to
                motivate learning.
              </p>
            </Copy>
            <div className={styles.linksContainer}>
              <Prompt className={styles.elementaryPrompt} clickHandler={() => history.push('/elementary')}>
                Review Elementary Resources
              </Prompt>
              <div className={styles.storeBadges}>
                <AppleAppStoreBadge />
                <GooglePlayStoreBadge />
              </div>
            </div>
          </TextPart>
        </LeftRightCard>
      )}
      <div className={styles.verticalWhiteSpace} />
      {middleSchoolSolutions && (
        <LeftRightCard textLeadEven>
          <ImagePart>
            <img className={styles.imageMaxWidth} src={secondarySolutionsImage} alt='peekaville map' />
          </ImagePart>
          <div className={styles.leftMarginHack}>
            <TextPart>
              <Copy>
                <h3 className={styles.preTitle}>6TH TO 12TH GRADE</h3>
              </Copy>
              <Title>Supporting Teen Well-Being and Resilience</Title>
              <Copy>
                <p>
                  Adolescence comes with heightened challenges—stress, anxiety, social pressures, and academic demands.
                  Peekapak’s research-backed program helps middle and high school students build resilience, manage
                  emotions, and develop the confidence they need to navigate school and life.
                </p>
              </Copy>
              <Prompt clickHandler={() => history.push('/secondary')}>Review Secondary Resources</Prompt>
            </TextPart>
          </div>
        </LeftRightCard>
      )}
      <div className={styles.verticalWhiteSpace} />
      {systemWideData && (
        <LeftRightCard imageLead>
          <ImagePart>
            <img style={{ maxWidth: '500px' }} src={dataUsageImage} alt='classroom interaction' />
          </ImagePart>
          <div className={styles.rightMarginHack}>
            <TextPart>
              <Copy>
                <h3 className={styles.preTitle}>FOR CLASSROOMS, SCHOOL AND DISTRICTS</h3>
              </Copy>
              <Title>Real-Time Insights</Title>
              <Copy>
                <p>
                  Understand your students&lsquo; emotions and skill comprehension to foster a supportive learning
                  environment.
                </p>
                <ul className={styles.dataUsageList}>
                  <li>Track Student Emotions with Daily Feelings Check-Ins</li>
                  <li>Monitor Student Skill Comprehension</li>
                  <li>Identify Trends in Student Feelings & Emotional Well-Being</li>
                  <li>Easily Share Reports with Administrators</li>
                </ul>
              </Copy>
            </TextPart>
          </div>
        </LeftRightCard>
      )}
    </section>
  );
};

export default HighlightsSegment;

HighlightsSegment.defaultProps = defaultProps;
